import React, { useEffect, useState } from "react";
import { Modal } from "antd";
import cx from "classnames";
import jwt from "../../utils/jwt";
import {
  AGREEMENT_TRACKING_PATH,
  fetchPendingAgreementMappings,
  formatValidity,
  getBusinessModel,
  isTrackAgreementsModalSnoozed,
  markAgreementItemsViewed,
  snoozeTrackAgreementsModal,
} from "../../utils/agreementMapping";
import useAgreementItemActions from "./useAgreementItemActions";
import ItemAction from "./ItemAction";
import styles from "./agreementTracking.module.scss";

const GRID_COLUMNS = { gridTemplateColumns: "1.4fr 1.1fr 1.1fr 1.1fr 0.8fr" };

// Header remounts on every page, so the pending check runs once per login
// (keyed by token issue time and business model) rather than on each navigation.
let checkedFor = null;

const AgreementBlock = ({ agreement, actions }) => {
  const [collapsed, setCollapsed] = useState(false);
  return (
    <div className={styles.agreementBlock}>
      <div className={styles.agreementHeader}>
        <div>
          <span>{agreement.name}</span>
          <button onClick={() => actions.viewAgreement(agreement)}>
            View Agreement
          </button>
        </div>
        <button
          className={cx({ [styles.collapsed]: collapsed })}
          onClick={() => setCollapsed(!collapsed)}
          aria-label={collapsed ? "Expand" : "Collapse"}
        >
          ▼
        </button>
      </div>
      {collapsed ? null : (
        <div className={styles.table}>
          <div className={cx(styles.row, styles.headerRow)} style={GRID_COLUMNS}>
            <div>Contract/Addendum Name</div>
            <div>Type</div>
            <div>Validity</div>
            <div>Action</div>
            <div>View Details</div>
          </div>
          <div className={styles.children}>
            {agreement.items.map((item) => (
              <div className={styles.row} style={GRID_COLUMNS} key={item.id}>
                <div>{item.name}</div>
                <div>{item.typeLabel}</div>
                <div>{formatValidity(item.validFrom, item.validTill)}</div>
                <div>
                  <ItemAction item={item} actions={actions} />
                </div>
                <div>
                  <button
                    className={styles.viewButton}
                    onClick={() => actions.viewItem(item)}
                  >
                    View
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const TrackAgreementsModal = () => {
  const [agreements, setAgreements] = useState([]);
  const [shownItems, setShownItems] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const businessModel = getBusinessModel();
    const decodedToken = jwt.decode(localStorage.getItem("idToken"));
    const checkKey = decodedToken && `${businessModel}:${decodedToken.iat}`;
    if (
      !businessModel ||
      checkedFor === checkKey ||
      window.location.pathname === AGREEMENT_TRACKING_PATH ||
      isTrackAgreementsModalSnoozed()
    ) {
      return;
    }
    checkedFor = checkKey;

    let cancelled = false;
    let finished = false;
    fetchPendingAgreementMappings().then((pending) => {
      finished = true;
      if (cancelled || pending.length === 0) return;
      setAgreements(pending);
      setShownItems(pending.flatMap((agreement) => agreement.items));
      setOpen(true);
    });
    return () => {
      cancelled = true;
      // Unmounted before the check finished (page change), let the next
      // mount run it again.
      if (!finished) checkedFor = null;
    };
  }, []);

  // The 24 hour window starts when the operator dismisses the modal.
  const dismiss = () => {
    snoozeTrackAgreementsModal();
    markAgreementItemsViewed(shownItems);
    setOpen(false);
  };

  const actions = useAgreementItemActions((respondedItem) => {
    const remaining = agreements
      .map((agreement) => ({
        ...agreement,
        items: agreement.items.filter((item) => item.id !== respondedItem.id),
      }))
      .filter((agreement) => agreement.items.length > 0);
    setAgreements(remaining);
    if (remaining.length === 0) {
      dismiss();
    }
  });

  if (!open) return null;

  return (
    <>
      <Modal
        open
        centered
        width={780}
        footer={null}
        onCancel={dismiss}
        title={<span className={styles.modalTitle}>Track Agreements</span>}
      >
        <div className={styles.trackModal}>
          {agreements.map((agreement) => (
            <AgreementBlock
              key={agreement.id}
              agreement={agreement}
              actions={actions}
            />
          ))}
        </div>
      </Modal>
      {actions.modals}
    </>
  );
};

export default TrackAgreementsModal;
